import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { extractApiError } from '../../lib/api';
import {
  downloadAppointmentsCsv,
  useEmployeesPerformanceReport,
  useRevenueReport,
  useTopServicesReport,
} from './api';
import { presetRange, type PresetId, type Range } from './datePresets';

const PRESETS: PresetId[] = ['thisMonth', 'last7Days', 'last30Days', 'thisQuarter', 'custom'];

const CHART_COLORS = ['#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

function formatPln(value: string): string {
  const n = Number(value);
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatPercent(value: string | null): string {
  if (value === null) return '—';
  return `${value}%`;
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

export function ReportsPage(): ReactNode {
  const { t } = useTranslation();

  const [presetId, setPresetId] = useState<PresetId>('thisMonth');
  const [customRange, setCustomRange] = useState<Range>(() => presetRange('thisMonth'));

  const range: Range = useMemo(
    () => (presetId === 'custom' ? customRange : presetRange(presetId)),
    [presetId, customRange],
  );

  const revenue = useRevenueReport(range);
  const top = useTopServicesReport(range, 3);
  const employees = useEmployeesPerformanceReport(range);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport(): Promise<void> {
    setExporting(true);
    setExportError(null);
    try {
      await downloadAppointmentsCsv(range);
    } catch (err) {
      setExportError(extractApiError(err));
    } finally {
      setExporting(false);
    }
  }

  const isLoading = revenue.isLoading || top.isLoading || employees.isLoading;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{t('reports.title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('reports.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-60"
        >
          {exporting ? t('reports.exporting') : t('reports.exportCsv')}
        </button>
      </header>

      {exportError && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">
          {exportError}
        </div>
      )}

      {/* Presety dat + custom. */}
      <section className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((id) => {
            const active = presetId === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setPresetId(id)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  active
                    ? 'bg-brand-600 text-white'
                    : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                }`}
              >
                {t(`reports.presets.${id}`)}
              </button>
            );
          })}
        </div>
        {presetId === 'custom' && (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              <span className="text-zinc-600">{t('reports.from')}</span>
              <input
                type="date"
                value={customRange.from}
                max={customRange.to}
                onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
                className="rounded-md border border-zinc-300 bg-white px-2 py-1"
              />
            </label>
            <label className="flex items-center gap-2">
              <span className="text-zinc-600">{t('reports.to')}</span>
              <input
                type="date"
                value={customRange.to}
                min={customRange.from}
                onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
                className="rounded-md border border-zinc-300 bg-white px-2 py-1"
              />
            </label>
          </div>
        )}
        <div className="mt-2 text-xs text-zinc-500">
          {t('reports.rangeInfo', { from: range.from, to: range.to })}
        </div>
      </section>

      {isLoading && (
        <div className="rounded-md border border-zinc-200 bg-white p-6 text-sm text-zinc-500">
          {t('common.loading')}
        </div>
      )}

      {revenue.error && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">
          {extractApiError(revenue.error)}
        </div>
      )}

      {revenue.data && (
        <>
          {/* KPI. */}
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label={t('reports.kpi.revenue')}
              value={formatPln(revenue.data.totalRevenue)}
              hint={t('reports.kpi.revenueHint')}
            />
            <KpiCard
              label={t('reports.kpi.appointments')}
              value={String(revenue.data.appointmentsCount)}
              hint={t('reports.kpi.appointmentsHint')}
            />
            <KpiCard
              label={t('reports.kpi.avgValue')}
              value={formatPln(revenue.data.averageAppointmentValue)}
              hint={t('reports.kpi.avgValueHint')}
            />
            <KpiCard
              label={t('reports.kpi.noShow')}
              value={formatPercent(revenue.data.noShowRate)}
              hint={t('reports.kpi.noShowHint', {
                count: revenue.data.noShowCount,
              })}
            />
          </section>

          {/* Wykres przychodu po dniach. */}
          <section className="rounded-lg border border-zinc-200 bg-white p-4">
            <h2 className="mb-3 text-base font-semibold text-zinc-900">
              {t('reports.charts.revenueByDay')}
            </h2>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={revenue.data.byDay.map((d) => ({
                    date: formatShortDate(d.date),
                    revenue: Number(d.revenue),
                  }))}
                  margin={{ top: 10, right: 10, bottom: 10, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v: number) => formatPln(v.toFixed(2))}
                    labelClassName="text-xs"
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#7c3aed"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}

      {/* Top usługi. */}
      {top.data && (
        <section className="rounded-lg border border-zinc-200 bg-white p-4">
          <h2 className="mb-3 text-base font-semibold text-zinc-900">
            {t('reports.charts.topServices')}
          </h2>
          {top.data.items.length === 0 ? (
            <div className="text-sm text-zinc-500">{t('reports.empty')}</div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={top.data.items.map((s) => ({
                    name: s.name,
                    revenue: Number(s.revenue),
                    count: s.count,
                  }))}
                  layout="vertical"
                  margin={{ top: 5, right: 20, bottom: 5, left: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={120}
                  />
                  <Tooltip
                    formatter={(v: number) => formatPln(v.toFixed(2))}
                  />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {top.data.items.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      )}

      {/* Performance pracowników — tabela. */}
      {employees.data && (
        <section className="rounded-lg border border-zinc-200 bg-white p-4">
          <h2 className="mb-3 text-base font-semibold text-zinc-900">
            {t('reports.charts.employeesPerformance')}
          </h2>
          {employees.data.items.length === 0 ? (
            <div className="text-sm text-zinc-500">{t('reports.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <th className="py-2 pr-4">{t('reports.table.employee')}</th>
                    <th className="py-2 pr-4 text-right">{t('reports.table.appointments')}</th>
                    <th className="py-2 pr-4 text-right">{t('reports.table.revenue')}</th>
                    <th className="py-2 pr-4 text-right">{t('reports.table.avgValue')}</th>
                    <th className="py-2 pr-4 text-right">{t('reports.table.noShow')}</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.data.items.map((e) => (
                    <tr key={e.userId} className="border-b border-zinc-100 last:border-0">
                      <td className="py-2 pr-4">
                        <span className="flex items-center gap-2">
                          {e.color && (
                            <span
                              className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: e.color }}
                            />
                          )}
                          <span className="font-medium text-zinc-900">
                            {e.firstName} {e.lastName}
                          </span>
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {e.appointmentsCount}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {formatPln(e.revenue)}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-zinc-600">
                        {formatPln(e.averageAppointmentValue)}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-zinc-600">
                        {formatPercent(e.noShowRate)}{' '}
                        <span className="text-xs text-zinc-400">
                          ({e.noShowCount})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): ReactNode {
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200">
      <div className="text-sm font-medium text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-brand-700">{value}</div>
      {hint && <div className="mt-1 text-xs text-zinc-400">{hint}</div>}
    </div>
  );
}
